import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Truck, SignOut, Database, Flask, Sparkle, List, X, Lifebuoy } from "@phosphor-icons/react";
import CopilotPanel from "../../components/CopilotPanel";
import DemoExperience from "../../components/demo/DemoExperience";
import SidebarNav from "../../components/admin/SidebarNav";
import WorkspaceTabsBar from "../../components/admin/WorkspaceTabsBar";
import Breadcrumbs from "../../components/admin/Breadcrumbs";
import CommandPalette from "../../components/CommandPalette";
import NotificationBell from "../../components/admin/NotificationBell";



export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [env, setEnv] = useState({ demo_mode: true, system_mode: "assisted" });
  const [badges, setBadges] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/data/settings");
        if (alive) setEnv(data);
      } catch { /* no-op */ }
      try {
        const { data } = await api.get("/nav/badges");
        if (alive) setBadges(data || {});
      } catch { /* no-op */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Close drawer on route change (mobile users tap a nav link → drawer should hide).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // ESC closes drawer + lock body scroll while it's open.
  useEffect(() => {
    if (!mobileOpen) {
      document.body.classList.remove("rf-no-scroll");
      return;
    }
    document.body.classList.add("rf-no-scroll");
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    // Focus the drawer for accessibility.
    drawerRef.current?.focus?.();
    return () => {
      document.body.classList.remove("rf-no-scroll");
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  const modeColor = env.system_mode === "full_autonomous" ? "#16A34A"
                  : env.system_mode === "assisted" ? "#CA8A04"
                  : "#737373";
  const modeLabel = env.system_mode === "full_autonomous" ? "AUTONOMOUS"
                  : (env.system_mode || "assisted").toUpperCase();

  return (
    <div className="min-h-screen flex flex-col w-full max-w-full overflow-x-hidden">
      {/* Top status bar — always visible. On mobile it also hosts the hamburger. */}
      <div
        className="px-3 sm:px-4 py-2 flex items-center justify-between border-b border-neutral-800 text-xs gap-3"
        style={{ background: "#0A0A0A", color: "#fff" }}
        data-testid="system-status-bar"
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 -ml-2 text-white hover:bg-neutral-800 rounded"
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            aria-controls="rf-admin-drawer"
            data-testid="mobile-nav-toggle"
          >
            <List size={20} weight="bold" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-block rounded-full shrink-0" style={{ width: 8, height: 8, background: modeColor }} />
            <span className="font-mono uppercase truncate" style={{ color: modeColor }}>MODE: {modeLabel}</span>
          </div>
          {user?.is_demo && (
            <span
              data-testid="demo-mode-badge"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "rgba(229,9,20,0.15)", color: "#F87171", border: "1px solid rgba(229,9,20,0.4)" }}
              title="Demo Mode — read-only, sandboxed"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-90" />
              Demo Mode
            </span>
          )}
          <Link to="/admin/data" className="hidden sm:flex items-center gap-2 hover:text-white min-w-0">
            {env.demo_mode
              ? <><Flask size={12} weight="fill" style={{ color: "#FCD34D" }} /> <span className="truncate" style={{ color: "#FCD34D" }}>DEMO MODE</span></>
              : <><Database size={12} weight="fill" style={{ color: "#16A34A" }} /> <span className="truncate" style={{ color: "#16A34A" }}>LIVE MODE</span></>}
          </Link>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              // Trigger the existing Cmd+K palette by dispatching the shortcut.
              const evt = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
              window.dispatchEvent(evt);
            }}
            className="hidden md:inline-flex items-center gap-1.5 text-neutral-500 hover:text-white font-mono text-[10px] uppercase tracking-widest border border-neutral-800 rounded px-2 py-1 transition-colors"
            data-testid="cmdk-trigger"
            title="Command palette (⌘K)"
          >
            <span className="rf-kbd">⌘</span>K Search
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("rf:show-launch"))}
            className="hidden md:flex items-center gap-1 text-neutral-500 hover:text-white font-mono text-[10px] uppercase tracking-widest"
            data-testid="show-launch-experience"
            title="Show launch experience"
          >
            <Sparkle size={10} weight="fill" /> What&apos;s New
          </button>
          <NotificationBell />
          <div className="text-neutral-500 font-mono hidden lg:block">Roll Fast Solutions LLC · TMS</div>
          <div className="text-neutral-500 font-mono lg:hidden text-[10px] truncate">Roll Fast TMS</div>
        </div>
      </div>

      {/* Body: sidebar + main. Uses flex on md+, stacked-with-overlay on mobile. */}
      <div className="flex flex-1 min-h-0 w-full max-w-full">
        {/* Backdrop — mobile only, only when drawer open. */}
        {mobileOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 md:hidden"
            data-testid="mobile-nav-backdrop"
          />
        )}

        {/* Sidebar.
            Mobile (<md): fixed off-canvas drawer, 280px, slides from left.
            Tablet (md): static, icon-only 64px column.
            Desktop (lg+): static, full 256px with labels. */}
        <aside
          id="rf-admin-drawer"
          ref={drawerRef}
          tabIndex={-1}
          className={`rf-drawer fixed md:static inset-y-0 left-0 z-50 md:z-auto
                      flex flex-col text-white shrink-0
                      w-[280px] md:w-16 lg:w-64
                      transform transition-transform duration-300 ease-out
                      ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                      md:translate-x-0
                      overflow-y-auto overflow-x-hidden`}
          style={{ background: "#0A0A0A" }}
          aria-hidden={!mobileOpen && typeof window !== "undefined" && window.innerWidth < 768}
          data-testid="admin-sidebar"
        >
          <div className="flex items-center gap-3 px-2 py-3 mb-2 border-b border-neutral-800 md:px-1 lg:px-2">
            <img src="/logo.png?v=rf2026-newlogo-0804" alt="Roll Fast Solutions logo"
                 className="w-9 h-9 object-contain shrink-0" data-testid="brand-logo" />
            <div className="md:hidden lg:block min-w-0">
              <div className="label-tag text-neutral-500">Roll Fast</div>
              <div className="font-display font-black tracking-tight truncate">ROLL FAST TMS</div>
            </div>
            {/* Close button — mobile drawer only. */}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="ml-auto md:hidden p-2 text-neutral-400 hover:text-white"
              aria-label="Close navigation"
              data-testid="mobile-nav-close"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
          <SidebarNav userRole={user?.role} userId={user?.id} badges={badges} />
          <div className="mt-auto pt-4 px-2 md:px-1 lg:px-2 border-t border-neutral-800 pb-4">
            <Link
              to="/support"
              className="sidebar-link md:justify-center lg:justify-start"
              data-testid="nav-support"
              title="Support"
            >
              <Lifebuoy size={16} weight="bold" />
              <span className="md:hidden lg:inline">Support</span>
            </Link>
            <div className="px-2 mt-3 mb-3 md:hidden lg:block min-w-0">
              <div className="label-tag text-neutral-500">Signed in</div>
              <div className="text-sm font-bold truncate">{user?.name}</div>
              <div className="text-xs text-neutral-400 font-mono truncate">{user?.role}</div>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="sidebar-link w-full md:justify-center lg:justify-start"
              title="Sign out"
            >
              <SignOut size={16} weight="bold" />
              <span className="md:hidden lg:inline">Sign out</span>
            </button>
          </div>
        </aside>

        {/* Main — always full available width. min-w-0 prevents flex children from
            forcing a horizontal scrollbar on wide tables; overflow-x-auto on wrappers
            inside individual pages handles intentional table scroll. */}
        <main
          id="main-content"
          tabIndex={-1}
          role="main"
          aria-label="Roll Fast admin content"
          className="flex-1 min-w-0 w-full max-w-full overflow-y-auto overflow-x-hidden text-white"
          style={{ background: "#050505" }}
          data-testid="admin-main"
        >
          <WorkspaceTabsBar isAdmin={user?.role === "admin"} />
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
      <CopilotPanel />
      <DemoExperience />
      <CommandPalette />
    </div>
  );
}
